import React, { createContext, useContext, useEffect, useRef, useCallback, useState } from 'react';
import { apiClient } from '../utils/api';
import { setJobRegistrar } from '../utils/jobRegistry';

const POLL_INTERVAL_MS = 3000;
const MAX_AGE_MS = 60 * 60 * 1000; // prune jobs older than 1 hour from storage
const STORAGE_KEY = 'selectivate_active_jobs';

const JobContext = createContext(null);

// ---------- localStorage helpers ----------

function loadJobs() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw);
    // Prune stale entries
    const now = Date.now();
    Object.keys(parsed).forEach(id => {
      if (now - (parsed[id].startedAt || 0) > MAX_AGE_MS) delete parsed[id];
    });
    return parsed;
  } catch {
    return {};
  }
}

function saveJobs(jobs) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(jobs));
  } catch {}
}

// ---------- Provider ----------

export function JobProvider({ children }) {
  // jobs shape: { [jobId]: { label, status, result, error, startedAt, projectId } }
  const [jobs, setJobs] = useState(() => loadJobs());
  const listenersRef = useRef({}); // { [jobId]: Set<(job) => void> }
  const timerRef = useRef(null);

  // Persist whenever jobs change
  useEffect(() => {
    saveJobs(jobs);
  }, [jobs]);

  // Single global polling loop
  useEffect(() => {
    const poll = async () => {
      const pending = Object.entries(jobs).filter(
        ([, j]) => j.status === 'pending' || j.status === 'processing'
      );
      if (pending.length === 0) return;

      await Promise.allSettled(
        pending.map(async ([jobId]) => {
          try {
            const res = await apiClient.get(`/jobs/${jobId}`);
            const job = res.data;
            if (!job) return;

            setJobs(prev => {
              const updated = { ...prev, [jobId]: { ...prev[jobId], ...job } };
              saveJobs(updated);
              return updated;
            });

            // Notify per-job listeners
            const listeners = listenersRef.current[jobId];
            if (listeners) listeners.forEach(fn => fn(job));
          } catch {
            // Network hiccup — keep polling
          }
        })
      );
    };

    timerRef.current = setInterval(poll, POLL_INTERVAL_MS);
    return () => clearInterval(timerRef.current);
  }, [jobs]);

  /**
   * Register a new job. Returns a Promise that resolves with job.result
   * when the job completes, or rejects on failure — regardless of navigation.
   */
  const registerJob = useCallback((jobId, label = 'Processing…', meta = {}) => {
    setJobs(prev => ({
      ...prev,
      [jobId]: {
        job_id: jobId,
        label,
        status: 'pending',
        result: null,
        error: null,
        startedAt: Date.now(),
        ...meta,
      },
    }));

    return new Promise((resolve, reject) => {
      if (!listenersRef.current[jobId]) {
        listenersRef.current[jobId] = new Set();
      }
      const handler = (job) => {
        if (job.status === 'completed') {
          listenersRef.current[jobId]?.delete(handler);
          resolve(job.result);
        } else if (job.status === 'failed') {
          listenersRef.current[jobId]?.delete(handler);
          reject(new Error(job.error_message || 'Job failed'));
        }
      };
      listenersRef.current[jobId].add(handler);
    });
  }, []);

  /**
   * Subscribe to updates for a specific job (e.g. from a component that
   * was not the one that started the job). Returns an unsubscribe function.
   */
  const subscribeToJob = useCallback((jobId, fn) => {
    if (!listenersRef.current[jobId]) {
      listenersRef.current[jobId] = new Set();
    }
    listenersRef.current[jobId].add(fn);
    return () => listenersRef.current[jobId]?.delete(fn);
  }, []);

  /** Dismiss/clear a completed or failed job from the list */
  const dismissJob = useCallback((jobId) => {
    setJobs(prev => {
      const next = { ...prev };
      delete next[jobId];
      return next;
    });
    delete listenersRef.current[jobId];
  }, []);

  // Populate the singleton registry so non-hook code (aiService) can reach this context
  useEffect(() => {
    setJobRegistrar(registerJob);
  }, [registerJob]);

  const value = { jobs, registerJob, subscribeToJob, dismissJob };

  return <JobContext.Provider value={value}>{children}</JobContext.Provider>;
}

export function useJobs() {
  const ctx = useContext(JobContext);
  if (!ctx) throw new Error('useJobs must be used inside JobProvider');
  return ctx;
}

/**
 * Convenience hook for a single job.
 * Usage: const { job, isRunning } = useJob(jobId);
 */
export function useJob(jobId) {
  const { jobs } = useJobs();
  const job = jobId ? jobs[jobId] : null;
  const isRunning = job?.status === 'pending' || job?.status === 'processing';
  return { job, isRunning };
}
